import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Fingerprint,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Copy,
  Check,
  Key,
} from 'lucide-react'
import { useAppStore } from '../store'
import { api } from '../lib/api'
import { VaultMeta, VaultData, createEmptyVaultData } from '../config'
import {
  fromBase64,
  deriveKeyFromPassphrase,
  deriveVerificationTag,
  constantTimeCompare,
  decryptVaultData,
  deriveCacheKey,
  toBase64,
} from '../lib/crypto'
import { authenticateWithPasskey } from '../lib/passkey'
import { getCachedVaultData, cacheVaultData, isVaultCached } from '../lib/cache'
import { VaultBrowser } from '../components/VaultBrowser'
import { KeyCard, KeyCardStack } from '../components/vault/KeyCard'
import { groupVaultId } from '../lib/folderColors'

export function VaultPage() {
  const { vaultId } = useParams<{ vaultId: string }>()
  const navigate = useNavigate()

  const unlockedVault = useAppStore((s) => s.unlockedVault)
  const setUnlockedVault = useAppStore((s) => s.setUnlockedVault)
  const addBookmarkedVault = useAppStore((s) => s.addBookmarkedVault)

  const [meta, setMeta] = useState<VaultMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [unlocking, setUnlocking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [passphrase, setPassphrase] = useState('')
  const [showPassphrase, setShowPassphrase] = useState(false)
  const [isCached, setIsCached] = useState(false)
  const [errorCount, setErrorCount] = useState(0)
  const [linkCopied, setLinkCopied] = useState(false)

  // Re-trigger the wobble each time an unlock attempt fails.
  useEffect(() => {
    if (error && meta) setErrorCount((n) => n + 1)
  }, [error, meta])

  // Check if already unlocked
  const isUnlocked = unlockedVault?.id === vaultId

  // Fetch vault metadata
  useEffect(() => {
    if (!vaultId) return

    const fetchMeta = async () => {
      setLoading(true)
      setError(null)

      try {
        const vaultMeta = await api.getVaultMeta(vaultId)
        if (!vaultMeta) {
          setError('Vault not found')
          setLoading(false)
          return
        }
        setMeta(vaultMeta)

        // Check if cached
        const cached = await isVaultCached(vaultId)
        setIsCached(cached)

        // Add to bookmarks if not already
        await api.addBookmark(vaultId)
        addBookmarkedVault(vaultId)
      } catch (err) {
        console.error('Failed to fetch vault:', err)
        setError('Failed to load vault')
      } finally {
        setLoading(false)
      }
    }

    fetchMeta()
  }, [vaultId, addBookmarkedVault])

  const handleUnlockWithPassphrase = async () => {
    if (!meta || !vaultId || unlocking) return

    setUnlocking(true)
    setError(null)

    try {
      const salt = fromBase64(meta.salt)
      const key = await deriveKeyFromPassphrase(passphrase, salt)
      const verificationTag = await deriveVerificationTag(key, salt)
      const storedTag = fromBase64(meta.verificationTag)

      // Verify passphrase
      if (!constantTimeCompare(verificationTag, storedTag)) {
        setError('Incorrect passphrase')
        setUnlocking(false)
        return
      }

      // Try to load from cache first
      const cacheKey = await deriveCacheKey(key, salt)
      let vaultData: VaultData
      let encryptedData: Uint8Array | null = null

      const cachedData = await getCachedVaultData(vaultId, cacheKey)
      if (cachedData) {
        try {
          const decrypted = await decryptVaultData(cachedData, key)
          vaultData = JSON.parse(decrypted)
        } catch {
          // Cache invalid, fetch fresh
          encryptedData = await api.downloadVaultData(vaultId, verificationTag)
          if (encryptedData.length > 0) {
            const decrypted = await decryptVaultData(encryptedData, key)
            vaultData = JSON.parse(decrypted)
          } else {
            vaultData = createEmptyVaultData()
          }
        }
      } else {
        // No cache, fetch from server
        encryptedData = await api.downloadVaultData(vaultId, verificationTag)
        if (encryptedData.length > 0) {
          const decrypted = await decryptVaultData(encryptedData, key)
          vaultData = JSON.parse(decrypted)
        } else {
          vaultData = createEmptyVaultData()
        }
      }

      // Cache the encrypted data for offline use
      if (encryptedData && encryptedData.length > 0) {
        await cacheVaultData(vaultId, encryptedData, cacheKey)
      }

      setUnlockedVault({
        id: vaultId,
        meta,
        data: vaultData,
        key,
        verificationTag,
        isDirty: false,
      })
    } catch (err) {
      console.error('Failed to unlock vault:', err)
      setError('Failed to decrypt vault data')
    } finally {
      setUnlocking(false)
    }
  }

  const handleUnlockWithPasskey = async () => {
    if (!meta || !vaultId || !meta.credentialId || unlocking) return

    setUnlocking(true)
    setError(null)

    try {
      const salt = fromBase64(meta.salt)
      const prfOutput = await authenticateWithPasskey(meta.credentialId, salt)

      if (!prfOutput) {
        setError('Passkey authentication failed')
        setUnlocking(false)
        return
      }

      const key = await deriveKeyFromPassphrase(toBase64(prfOutput), salt)
      const verificationTag = await deriveVerificationTag(key, salt)
      const storedTag = fromBase64(meta.verificationTag)

      if (!constantTimeCompare(verificationTag, storedTag)) {
        setError('Passkey verification failed')
        setUnlocking(false)
        return
      }

      // Fetch and decrypt vault data
      const encryptedData = await api.downloadVaultData(
        vaultId,
        verificationTag,
      )
      let vaultData: VaultData

      if (encryptedData.length > 0) {
        const decrypted = await decryptVaultData(encryptedData, key)
        vaultData = JSON.parse(decrypted)
      } else {
        vaultData = createEmptyVaultData()
      }

      // Cache for offline use
      const cacheKey = await deriveCacheKey(key, salt)
      if (encryptedData.length > 0) {
        await cacheVaultData(vaultId, encryptedData, cacheKey)
      }

      setUnlockedVault({
        id: vaultId,
        meta,
        data: vaultData,
        key,
        verificationTag,
        isDirty: false,
      })
    } catch (err) {
      console.error('Failed to unlock with passkey:', err)
      setError('Failed to authenticate with passkey')
    } finally {
      setUnlocking(false)
    }
  }

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 1600)
    } catch {
      /* clipboard unavailable */
    }
  }

  // Unlocked state - show vault browser
  if (isUnlocked) {
    return <VaultBrowser />
  }

  // Vault not found
  if (error && !meta && !loading) {
    return (
      <div className="mx-auto max-w-md pt-10 text-center animate-fade-up">
        <span className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-3xl bg-[rgba(255,120,150,0.12)] text-[#FFA3B4]">
          <AlertCircle className="h-8 w-8" />
        </span>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Vault not found
        </h1>
        <p className="mt-2 text-muted">
          No vault with the ID{' '}
          <span className="font-mono text-white">{vaultId}</span> exists, or it
          has been deleted.
        </p>
        <button onClick={() => navigate('/')} className="btn-primary mt-6">
          Back to your vaults
        </button>
      </div>
    )
  }

  const isPasskey = meta?.authType === 'passkey'

  return (
    <div className="mx-auto w-full max-w-[400px] pb-8 pt-2 sm:pt-6">
      <KeyCardStack spread={unlocking}>
        <KeyCard
          vaultId={vaultId ? groupVaultId(vaultId) : ''}
          dim={loading}
          corner={
            isPasskey ? (
              <Fingerprint
                className="h-5 w-5 text-white/70"
                aria-label="Passkey vault"
              />
            ) : (
              <Key
                className="h-5 w-5 text-white/70"
                aria-label="Passphrase vault"
              />
            )
          }
          footerLeft={
            loading
              ? 'Fetching vault…'
              : isCached
                ? 'Available offline'
                : 'End-to-end encrypted'
          }
          footerRight={
            <span
              className={`inline-flex min-h-[28px] items-center gap-1.5 rounded-full px-3 text-[12.5px] font-bold transition-colors duration-300 ${
                unlocking
                  ? 'bg-vault-500 text-[#140D33]'
                  : 'bg-white/10 text-white'
              }`}
            >
              {unlocking ? 'Unlocking' : 'Locked'}
            </span>
          }
        />
      </KeyCardStack>

      <div
        key={errorCount}
        className={`mt-8 grid gap-3 ${errorCount > 0 ? 'animate-wobble' : 'animate-fade-up'}`}
        style={{ animationDelay: errorCount > 0 ? '0ms' : '350ms' }}
      >
        {loading ? (
          <div className="h-[54px] rounded-[18px] bg-[linear-gradient(90deg,rgba(255,255,255,0.04),rgba(255,255,255,0.09),rgba(255,255,255,0.04))] bg-[length:200%_100%] animate-[shimmer_1.4s_linear_infinite]" />
        ) : isPasskey ? (
          <button
            onClick={handleUnlockWithPasskey}
            disabled={unlocking}
            className="btn-primary min-h-[54px] rounded-[18px] text-[15.5px]"
          >
            {unlocking ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <Fingerprint className="h-5 w-5" />
            )}
            {unlocking ? 'Waiting for passkey…' : 'Unlock with passkey'}
          </button>
        ) : (
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (passphrase) handleUnlockWithPassphrase()
            }}
          >
            <label htmlFor="passphrase" className="sr-only">
              Passphrase
            </label>
            <div
              className={`input flex items-center gap-2 !rounded-[18px] !pr-1.5 ${error ? 'is-err' : ''}`}
            >
              <input
                id="passphrase"
                type={showPassphrase ? 'text' : 'password'}
                value={passphrase}
                onChange={(e) => {
                  setPassphrase(e.target.value)
                  setError(null)
                }}
                autoComplete="current-password"
                className="min-h-[52px] min-w-0 flex-1 bg-transparent tracking-[0.08em] outline-none placeholder:tracking-normal placeholder:text-faint"
                placeholder="Passphrase"
                autoFocus
                disabled={unlocking}
              />
              <button
                type="button"
                onClick={() => setShowPassphrase(!showPassphrase)}
                className="icon-btn !h-10 !w-10"
                aria-label={
                  showPassphrase ? 'Hide passphrase' : 'Show passphrase'
                }
              >
                {showPassphrase ? (
                  <EyeOff className="h-[18px] w-[18px]" />
                ) : (
                  <Eye className="h-[18px] w-[18px]" />
                )}
              </button>
            </div>
            <button
              type="submit"
              disabled={!passphrase || unlocking}
              className="btn-primary min-h-[54px] rounded-[18px] text-[15.5px]"
            >
              {unlocking && <RefreshCw className="h-5 w-5 animate-spin" />}
              {unlocking ? 'Decrypting…' : 'Unlock'}
            </button>
          </form>
        )}

        {error && (
          <p role="alert" className="text-center text-sm text-[#FFA3B4]">
            {error}
          </p>
        )}
      </div>

      <div
        className="mt-8 flex items-center gap-2 rounded-2xl border border-white/[0.07] bg-white/[0.03] py-1.5 pl-4 pr-1.5 animate-fade-up"
        style={{ animationDelay: '450ms' }}
      >
        <span className="min-w-0 flex-1 text-[13px] text-faint">
          Share this link with anyone who knows the{' '}
          {isPasskey ? 'passkey' : 'passphrase'}.
        </span>
        <button
          type="button"
          onClick={copyShareLink}
          className="btn-ghost !min-h-[40px] !px-3 text-[13px]"
        >
          {linkCopied ? (
            <Check className="h-4 w-4 text-[#7FE0B0]" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
          {linkCopied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  )
}
